'use client';
import { useMemo, useRef, useState } from 'react';
import { CircleAlert, ShieldCheck, TriangleAlert, Users, SlidersHorizontal, Link2, KeyRound, Info, MessagesSquare, Mic, Image as ImageIcon, type LucideIcon } from 'lucide-react';
import { MonacoEditor } from '../../lib/monaco/monacoLoader';
import { defineEditorThemes, editorTheme } from '../../lib/monaco/oledTheme';
import { StatusMark } from '../../components/ui/StatusMark';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/shadcn/textarea';
import { Toggle } from '../../components/ui/Toggle';
import { BrainModelField } from '../../components/ui/BrainModelField';
import { ChoiceField } from '../../components/ui/ChoiceField';
import { useTranslation } from '../../lib/i18n';
import { useBrainModels } from '../../lib/queries';
import { chatModelCatalog } from '../../lib/modelCatalog';
import { embeddingModelCatalog } from '../../lib/modelProvider';
import type { PluginConfigField, RolePolicy } from '../../lib/types';
import type { PluginConfigEditorProps } from 'elowen-plugin-ui-kit';
import { RISK_TONE, riskLabel } from './pluginDetail.shared';
import { numberOutOfBounds, requiredConfigFieldMissing } from '../../lib/usePluginConfigDraft';
import { saveHalted } from '../../lib/useAutoSaveStatus';
import { Spinner } from '../../components/ui/states';
import { SettingsGroup, SettingsRow } from '../../components/ui/SettingsSurface';
import { EmptyState } from '../../components/ui/states';
import { FieldLegend, FieldSet } from '../../components/ui/shadcn/field';
import { PluginAccessCard, pluginAccessStatus } from './PluginAccess';
import { PluginActivationPending } from './PluginActivationPending';
import { SliderWithReading } from '../../components/ui/SliderWithReading';
import { DestinationField, UserField, MultiSelectField, TimezoneField, PluginTokenListField, ProjectsField, PluginsField, ToolsField, ModelsField, PluginProviderField } from './PluginConfigFieldPickers';
import { RolePoliciesEditor } from './RolePoliciesEditor';
import { ModalFieldRow } from './ModalFieldRow';
import { numberDivisor, displayNumber, displayBound, displayPlaceholder, decimalPlaces, sliderCurrentValid, sliderCanonicalValue } from './pluginConfigNumber';

// A settings-group icon for an author-declared config section, inferred from its key/label. Falls back to
// the generic behavior glyph so every section card carries an icon-chip header like the rest of settings.
const SECTION_ICONS: { test: RegExp; icon: LucideIcon }[] = [
  { test: /connection|auth|credential|token|connect/, icon: Link2 },
  { test: /voice|audio|speech|tts|stt/, icon: Mic },
  { test: /conversation|context|history|memory/, icon: MessagesSquare },
  { test: /media|image|photo|file/, icon: ImageIcon },
  { test: /role|policies|policy|permission|access/, icon: Users },
];
function sectionIcon(field?: PluginConfigField): LucideIcon {
  if (!field) return SlidersHorizontal;
  const key = `${field.key} ${field.label}`.toLowerCase();
  return SECTION_ICONS.find((s) => s.test.test(key))?.icon ?? SlidersHorizontal;
}

/** Field types whose editing surface is a DOCUMENT (free text, code, a prompt, JSON) or a list of
 *  structured entries. None of them fits the one compact control a settings record allows, so the record
 *  carries a summary trigger and the editor keeps its full surface inside a modal. */
const MODAL_FIELD_TYPES = new Set<PluginConfigField['type']>(['textarea', 'code', 'prompt', 'json', 'rolePolicies']);

/** Field types that are a managed selection: its summary card (count, chips, Manage) is a block, not one
 *  line, so the record stacks the control at the band's full width like the Users drawer's grants. */
const MANAGED_SELECTION_FIELD_TYPES = new Set<PluginConfigField['type']>(['projects', 'plugins', 'tools', 'multiSelect']);

/** A `json` field is stored as the raw text the user typed, but an older config may still hold the parsed
 *  value — both have to render into the same editor. */
const jsonText = (raw: unknown): string => (typeof raw === 'string' ? raw : raw === undefined ? '' : JSON.stringify(raw, null, 2));
/** The schema without `keys`, and without a section those leave empty. A section that never had inputs
 *  is intentional documentation (an information card) and stays. */
function withoutFields(schema: PluginConfigField[], keys: ReadonlySet<string>): PluginConfigField[] {
  if (keys.size === 0) return schema;
  return schema.filter((field, index) => {
    if (field.type !== 'section') return !keys.has(field.key);
    const next = schema.findIndex((other, otherIndex) => otherIndex > index && other.type === 'section');
    const children = schema.slice(index + 1, next === -1 ? schema.length : next);
    return children.length === 0 || children.some((child) => !keys.has(child.key));
  });
}

const isJsonInvalid = (text: string): boolean => {
  if (text.trim() === '') return false;
  try { JSON.parse(text); return false; } catch { return true; }
};

/** The schema-driven config editor: a form generated from the manifest's `configSchema`, rendered as
 *  one Config collapsible or one collapsible per declared `section`. Every field is one canonical
 *  settings record — label on the left, ONE compact control on the right — so a plugin's settings read
 *  as the same table as the rest of the app. Secrets are write-only (stored values are reported but
 *  never shown back) and saving uses the revision-safe config draft. */
type PluginConfigMode = NonNullable<PluginConfigEditorProps['mode']>;

export function PluginConfigEditor({ name, detail, draft, mode = 'behavior', layout = 'groups' }: PluginConfigEditorProps) {
  const { t, locale } = useTranslation();
  const translated = detail.i18n?.[locale];
  const fieldLabel = (field: PluginConfigField) => translated?.fields?.[field.key]?.label ?? field.label;
  const fieldHint = (field: PluginConfigField) => translated?.fields?.[field.key]?.hint ?? field.hint;
  const fieldHelp = (field: PluginConfigField) => translated?.fields?.[field.key]?.help ?? field.help;
  const fieldOptions = (field: PluginConfigField) => (field.options ?? []).map((option) => ({
    ...option,
    label: translated?.fields?.[field.key]?.options?.[option.value] ?? option.label,
  }));
  const riskText = (risk: 'low' | 'medium' | 'high') => riskLabel(t, risk);
  const { data: brainModels } = useBrainModels();
  // The exact catalog the core embedding role offers, from the module that owns that decision.
  const embeddingCapableModels = useMemo(() => embeddingModelCatalog(brainModels ?? []), [brainModels]);
  // Everything that ends in a chat completion sees the chat half of the catalog; a `modelKind` field
  // takes the other half (see the `model` case). An OAuth account stays in both.
  const chatModels = useMemo(() => chatModelCatalog(brainModels ?? []), [brainModels]);
  const imageModels = useMemo(() => (brainModels ?? []).filter((m) => m.kind === 'image' || m.outputs?.includes('image')), [brainModels]);
  // The provider a `modelKind` picker narrows to is the one this same schema already asks for, so the
  // field needs no second attribute naming it.
  const providerFieldKey = useMemo(() => detail.configSchema?.find((f) => f.type === 'provider')?.key, [detail.configSchema]);
  const { values, setValue: set } = draft;
  const [replacingSecrets, setReplacingSecrets] = useState<Set<string>>(new Set());
  const [secretDrafts, setSecretDrafts] = useState<Record<string, string>>({});
  const [secretSaving, setSecretSaving] = useState<Set<string>>(new Set());
  // Secrets whose last commit failed. A refusal of the VALUE is told beside the field (`draft.errorKind`); any other
  // failure is the draft's own card. Either way the typed text stays in the input for another try.
  const [secretRefused, setSecretRefused] = useState<Set<string>>(new Set());
  // Secrets whose accepted write is durable but not yet active in the live registry.
  const [secretPending, setSecretPending] = useState<Set<string>>(new Set());
  // Synchronous guards, because blur follows Enter within one event turn, before React re-renders:
  // `secretInFlight` stops the second commit, `secretBlurSettled` swallows the blur that comes after a
  // finished commit or an Escape (including one fired while the input unmounts) until the user edits again.
  const secretInFlight = useRef(new Set<string>());
  const secretBlurSettled = useRef(new Set<string>());
  const dropKey = <V,>(record: Record<string, V>, key: string) => { const next = { ...record }; delete next[key]; return next; };
  const dropFromSet = (set: Set<string>, key: string) => { const next = new Set(set); next.delete(key); return next; };
  /** One write for a secret: the typed value, or `null` to erase the stored one ("replace with nothing"). The
   *  draft reports the outcome (a failed or ambiguous write is its card; a secret is never resent blindly), so
   *  this only keeps the typed text and marks a refused value. */
  const commitSecret = async (key: string, value: string | null) => {
    if (secretInFlight.current.has(key)) return;
    secretInFlight.current.add(key);
    setSecretRefused((current) => dropFromSet(current, key));
    setSecretSaving((current) => new Set(current).add(key));
    try {
      const outcome = await draft.commitValue(key, value);
      secretBlurSettled.current.add(key);
      setReplacingSecrets((current) => dropFromSet(current, key));
      setSecretDrafts((current) => dropKey(current, key));
      setSecretPending((current) => outcome.pending ? new Set(current).add(key) : dropFromSet(current, key));
    } catch {
      setSecretRefused((current) => new Set(current).add(key));
    } finally {
      secretInFlight.current.delete(key);
      setSecretSaving((current) => dropFromSet(current, key));
    }
  };
  /** Blur and Enter share this: a typed value is saved, an empty replacement erases the stored secret, and
   *  an empty unset field has nothing to save. */
  const settleSecret = (key: string, typed: string, replacing: boolean) => {
    if (secretBlurSettled.current.has(key)) return;
    if (typed.trim()) void commitSecret(key, typed);
    else if (replacing) void commitSecret(key, null);
  };
  const cancelSecret = (key: string) => {
    secretBlurSettled.current.add(key);
    setReplacingSecrets((current) => dropFromSet(current, key));
    setSecretDrafts((current) => dropKey(current, key));
    setSecretRefused((current) => dropFromSet(current, key));
  };

  /** The one compact control a record carries. Modal-backed types are not here — their record is built
   *  by {@link renderRow} instead. */
  const renderField = (f: PluginConfigField) => {
    switch (f.type) {
      case 'boolean':
        return <Toggle checked={values[f.key] === true} onChange={(v) => set(f.key, v)} label={fieldLabel(f)} />;
      case 'number': {
        const divisor = numberDivisor(f);
        const raw = values[f.key];
        const numeric = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : Number.NaN;
        const canonical = Number.isFinite(numeric) ? numeric : undefined;
        const displayed = divisor === 1 ? String(raw ?? '') : canonical === undefined ? String(raw ?? '') : String(displayNumber(f, canonical));
        const unit = f.display?.unit?.trim();
        if (f.display?.control === 'slider' && (raw === undefined || sliderCurrentValid(f, canonical))) {
          const fallback = typeof f.default === 'number' && Number.isFinite(f.default) ? f.default : f.min!;
          const sliderValue = displayNumber(f, canonical ?? fallback);
          const sliderMin = f.min! / divisor;
          const sliderMax = f.max! / divisor;
          const sliderStep = f.step! / divisor;
          const maximumFractionDigits = Math.min(20, Math.max(decimalPlaces(sliderMin), decimalPlaces(sliderStep)));
          const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits }).format(sliderValue);
          const readout = unit ? `${formatted} ${unit}` : formatted;
          return (
            <SliderWithReading
              value={sliderValue}
              min={sliderMin}
              max={sliderMax}
              step={sliderStep}
              onChange={(value) => set(f.key, sliderCanonicalValue(f, value))}
              aria-label={fieldLabel(f)}
              reading={readout}
              readingWidth="auto"
            />
          );
        }
        return (
          <Input
            type="number"
            unit={unit ?? ''}
            min={displayBound(f.min, divisor)}
            max={displayBound(f.max, divisor)}
            step={displayBound(f.step, divisor)}
            placeholder={displayPlaceholder(f)}
            aria-label={fieldLabel(f)}
            value={displayed}
            aria-invalid={numberOutOfBounds(f, raw) || undefined}
            onChange={(e) => set(f.key, e.target.value === '' ? null : Number(e.target.value) * divisor)}
          />
        );
      }
      case 'model': {
        if (f.modelKind === 'image') {
          // Image models of the provider this plugin is pointed at, and only those the account's model
          // allowlist enables — "enable on the account, then pick here". The value stays the bare model
          // id the image APIs take, so a config written before the picker existed still resolves, and an
          // empty value keeps meaning the provider's own default.
          const provider = providerFieldKey ? String(values[providerFieldKey] ?? '') : '';
          const offered = provider ? imageModels.filter((m) => m.provider === provider) : imageModels;
          return <BrainModelField value={String(values[f.key] ?? '')} onChange={(v) => set(f.key, v)} models={offered} title={fieldLabel(f)} defaultLabel={t.brain.imageModelDefault} keyOf={(m) => m.model} />;
        }
        // Brain-only picker: the shared modal/search catalog used by account and cron settings.
        return <BrainModelField value={String(values[f.key] ?? '')} onChange={(v) => set(f.key, v)} models={chatModels} title={fieldLabel(f)} defaultLabel={t.managePicker.none} allowDefault={!f.required} keyOf={(m) => m.exec} />;
      }
      case 'embeddingModel':
        // Same shared brain catalog, minus the OAuth accounts that cannot embed at all (see above).
        return <BrainModelField value={String(values[f.key] ?? '')} onChange={(v) => set(f.key, v)} models={embeddingCapableModels} title={fieldLabel(f)} defaultLabel={t.managePicker.none} allowDefault={!f.required} keyOf={(m) => m.exec} />;
      case 'provider':
        // Reuse a configured brain provider's key as this plugin's credentials (voice, image gen).
        return <PluginProviderField label={fieldLabel(f)} value={String(values[f.key] ?? '')} onChange={(v) => set(f.key, v)} providerType={f.providerType} />;
      case 'destination':
        return <DestinationField label={fieldLabel(f)} value={String(values[f.key] ?? '')} onChange={(v) => set(f.key, v)} />;
      case 'user':
        return <UserField label={fieldLabel(f)} value={String(values[f.key] ?? '')} required={f.required} onChange={(v) => set(f.key, v)} />;
      case 'projects': {
        const selected = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
        return <ProjectsField label={fieldLabel(f)} hint={fieldHint(f)} value={selected} onChange={(v) => set(f.key, v)} />;
      }
      case 'plugins': {
        const selected = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
        return <PluginsField label={fieldLabel(f)} hint={fieldHint(f)} value={selected} onChange={(v) => set(f.key, v)} />;
      }
      case 'tools': {
        const selected = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
        return <ToolsField label={fieldLabel(f)} hint={fieldHint(f)} value={selected} onChange={(v) => set(f.key, v)} />;
      }
      case 'models': {
        const selected = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
        return <ModelsField label={fieldLabel(f)} hint={fieldHint(f)} models={chatModels} value={selected} onChange={(v) => set(f.key, v)} />;
      }
      case 'timezone':
        return <TimezoneField label={fieldLabel(f)} hint={fieldHint(f)} value={String(values[f.key] ?? '')} onChange={(value) => set(f.key, value)} />;
      case 'tokenList':
        return <PluginTokenListField label={fieldLabel(f)} placeholder={f.placeholder} value={values[f.key]} browse={f.browse} onChange={(value) => set(f.key, value)} />;
      case 'enum': {
        const options = fieldOptions(f);
        const value = String(values[f.key] ?? '');
        if (value && !options.some((option) => option.value === value)) options.unshift({ value, label: value });
        return <ChoiceField title={fieldLabel(f)} options={options} value={value} onChange={(v) => set(f.key, v)} />;
      }
      case 'multiSelect': {
        const sel = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
        return <MultiSelectField label={fieldLabel(f)} options={fieldOptions(f)} value={sel} onChange={(v) => set(f.key, v)} />;
      }
      default:
        return <Input aria-label={fieldLabel(f)} value={String(values[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />;
    }
  };

  const editorOptions = { fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, automaticLayout: true, padding: { top: 12 }, wordWrap: 'on' as const, folding: false };

  /** The full editing surface a modal-backed field opens. */
  const renderEditor = (f: PluginConfigField) => {
    switch (f.type) {
      case 'textarea':
        return <Textarea aria-label={fieldLabel(f)} value={String(values[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} rows={14} />;
      case 'code':
        return (
          <MonacoEditor
            language={f.language ?? 'plaintext'}
            value={String(values[f.key] ?? '')}
            onChange={(v) => set(f.key, v ?? '')}
            theme={editorTheme()}
            beforeMount={defineEditorThemes}
            height="100%"
            options={{ ...editorOptions, ariaLabel: fieldLabel(f) }}
          />
        );
      case 'prompt':
        return (
          <MonacoEditor
            language="markdown"
            value={String(values[f.key] ?? '')}
            onChange={(v) => set(f.key, v ?? '')}
            theme={editorTheme()}
            beforeMount={defineEditorThemes}
            height="100%"
            options={{ ...editorOptions, lineNumbers: 'off', ariaLabel: fieldLabel(f) }}
          />
        );
      case 'json': {
        const text = jsonText(values[f.key]);
        const invalid = isJsonInvalid(text);
        return (
          <div className="flex flex-col gap-1">
            <Textarea
              aria-label={fieldLabel(f)}
              value={text}
              onChange={(e) => set(f.key, e.target.value)}
              rows={16}
              spellCheck={false}
              aria-invalid={invalid || undefined}
            />
            {invalid ? <span className="flex items-center gap-1 text-meta text-destructive" role="alert"><Info size={13} aria-hidden />{t.pluginCfg.invalidJson}</span> : null}
          </div>
        );
      }
      case 'rolePolicies':
        return (
          <RolePoliciesEditor
            value={Array.isArray(values[f.key]) ? (values[f.key] as RolePolicy[]) : []}
            onChange={(v) => set(f.key, v)}
            onRemove={(v) => draft.commitValue(f.key, v)}
            halted={saveHalted(draft.status)}
            owner={draft.owner}
            refused={draft.errorKind === 'validation'}
          />
        );
      default:
        return null;
    }
  };

  /** What a modal-backed record reports: HOW MUCH is stored, never the content itself. */
  const editorSummary = (f: PluginConfigField): string => {
    if (f.type === 'rolePolicies') {
      const count = Array.isArray(values[f.key]) ? (values[f.key] as unknown[]).length : 0;
      return count ? t.pluginCfg.editorItems.replace('{n}', String(count)) : t.pluginCfg.editorEmpty;
    }
    const text = f.type === 'json' ? jsonText(values[f.key]) : String(values[f.key] ?? '');
    return text.trim() ? t.pluginCfg.editorLines.replace('{n}', String(text.split('\n').length)) : t.pluginCfg.editorEmpty;
  };

  const renderRow = (f: PluginConfigField) => {
    const label = fieldLabel(f);
    const description = fieldHint(f);
    // Localized long-form help only appears when it adds to the localized one-line hint.
    const guidance = fieldHelp(f);
    const help = guidance?.trim() && guidance.trim() !== description?.trim() ? guidance : undefined;
    // The badge is the record's SHORT status, so it reads on the label's line like every other pill in a
    // settings card. It used to push the row onto the stacked layout with the control on a line of its
    // own, which doubled the height of every risk row (owner, 7 Sep 2026); that shape was for a risk enum
    // drawn as a segmented track, and a risk enum has been a compact select since.
    const risk = f.risk ? <StatusMark icon={{ low: ShieldCheck, medium: CircleAlert, high: TriangleAlert }[f.risk]} tone={RISK_TONE[f.risk]} label={riskText(f.risk)} /> : null;
    // The allowed range, shown only while the typed value would be refused: the daemon answers 400 to
    // it, and a footer that just says the save failed sent people looking for a broken connection.
    const range = f.type === 'number' && numberOutOfBounds(f, values[f.key])
      ? <span className="text-destructive" role="alert">{t.pluginCfg.numberRange.replace('{min}', String(displayBound(f.min, numberDivisor(f)) ?? '')).replace('{max}', String(displayBound(f.max, numberDivisor(f)) ?? ''))}</span>
      : null;

    if (MODAL_FIELD_TYPES.has(f.type)) {
      const invalid = f.type === 'json' && isJsonInvalid(jsonText(values[f.key]));
      return (
        <ModalFieldRow
          key={f.key}
          label={label}
          description={description}
          hint={help}
          status={risk || invalid ? (
            <span className="flex flex-wrap items-center gap-2">
              {risk}
              {invalid ? <span className="text-destructive" role="alert">{t.pluginCfg.invalidJson}</span> : null}
            </span>
          ) : undefined}
          summary={editorSummary(f)}
          saveState={draft}
          fillsModal={f.type === 'code' || f.type === 'prompt'}
        >
          {renderEditor(f)}
        </ModalFieldRow>
      );
    }

    if (f.type === 'secret') {
      const stored = detail.secretsSet.includes(f.key);
      // A stored secret that can no longer be decrypted is still stored, but it must be entered again.
      const unreadable = detail.secretsUnreadable?.includes(f.key) ?? false;
      // A stored secret is never shown back: the record reports that one exists and offers to replace it.
      if (stored && !replacingSecrets.has(f.key)) {
        return (
          <SettingsRow
            key={f.key}
            label={label}
            description={description}
            hint={[help, t.pluginCfg.secretKeepHint].filter(Boolean).join(' ')}
            status={(
              <span className="flex flex-wrap items-center gap-2">
                {risk}
                {unreadable
                  ? <StatusMark kind="error" tone="danger" label={t.pluginCfg.secretUnreadable} />
                  : <StatusMark kind="success" tone="success" label={t.pluginCfg.secretSet} />}
                <PluginActivationPending pending={secretPending.has(f.key)} />
              </span>
            )}
            actions={
              <Button type="button" variant="ghost" size="sm" icon={KeyRound} onClick={() => {
                secretBlurSettled.current.delete(f.key);
                setSecretRefused((current) => dropFromSet(current, f.key));
                setSecretDrafts((current) => ({ ...current, [f.key]: '' }));
                setReplacingSecrets((current) => new Set(current).add(f.key));
              }}>
                {t.pluginCfg.secretReplace}
              </Button>
            }
          />
        );
      }
      // An unset secret and a replacement commit through `draft.commitValue` on blur or Enter, never through
      // the generic controlled field: draft.setValue intentionally ignores secrets, which made a fresh
      // required credential appear editable while every keystroke was discarded.
      const refused = secretRefused.has(f.key) && draft.errorKind === 'validation';
      const saving = secretSaving.has(f.key);
      const replacing = stored && replacingSecrets.has(f.key);
      return (
        <SettingsRow
          key={f.key}
          label={label}
          description={description}
          hint={replacing ? [help, t.pluginCfg.secretReplaceHint].filter(Boolean).join(' ') : help}
          status={(
            <span className="flex flex-wrap items-center gap-2">
              {risk}
              {saving ? <Spinner size="sm" tone="" /> : null}
              <PluginActivationPending pending={secretPending.has(f.key)} />
            </span>
          )}
          control={(
            <Input
              type="password"
              aria-label={label}
              aria-invalid={refused || undefined}
              aria-busy={saving || undefined}
              readOnly={saving}
              value={secretDrafts[f.key] ?? ''}
              onChange={(e) => {
                secretBlurSettled.current.delete(f.key);
                setSecretDrafts((current) => ({ ...current, [f.key]: e.target.value }));
              }}
              onBlur={(e) => settleSecret(f.key, e.currentTarget.value, replacing)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); settleSecret(f.key, e.currentTarget.value, replacing); }
                else if (e.key === 'Escape' && replacing) { e.preventDefault(); cancelSecret(f.key); }
              }}
              placeholder={replacing ? t.pluginCfg.secretReplacementPlaceholder : t.pluginCfg.secretPlaceholder}
              autoComplete="off"
              autoFocus={replacing}
              // Escape belongs to the replacement (cancel), not to the dialog the settings page lives in.
              data-keyboard-claimed={replacing ? '' : undefined}
            />
          )}
        />
      );
    }

    return (
      <SettingsRow
        key={f.key}
        label={label}
        description={description}
        hint={help}
        status={risk || range ? <span className="flex flex-wrap items-center gap-2">{risk}{range}</span> : undefined}
        trailingLayout={MANAGED_SELECTION_FIELD_TYPES.has(f.type) ? 'stack' : undefined}
        control={renderField(f)}
      />
    );
  };

  const credentials = detail.credentials;
  const credentialKeys = new Set(credentials?.keys ?? []);
  // Placement is explicit; conditional rows still follow the current draft's visibleWhen value.
  const isVisible = (f: PluginConfigField) => f.display?.placement !== 'pluginForm' && (!f.visibleWhen || values[f.visibleWhen.key] === f.visibleWhen.equals);
  const fieldRows = (fields: PluginConfigField[]) => fields.filter(isVisible).map(renderRow);
  // A value the daemon refused without naming a field belongs to the submitted group, so the group says so
  // once. A bound the row already states (a number's range, malformed JSON) is that field's own message.
  const fieldStatesCause = (field: PluginConfigField) => numberOutOfBounds(field, values[field.key])
    || (field.type === 'json' && isJsonInvalid(jsonText(values[field.key])));
  const refusal = draft.errorKind === 'validation' && !detail.configSchema.some((field) => isVisible(field) && fieldStatesCause(field))
    ? <p role="alert" className="text-meta text-destructive">{t.pluginCfg.valueRefused}</p>
    : null;
  // Attention overrides visibility only: never overwrite the reader's remembered fold.
  const needsAttention = (fields: PluginConfigField[]) => fields.some((field) => isVisible(field) && (
    requiredConfigFieldMissing(field, values, detail.secretsSet)
    || numberOutOfBounds(field, values[field.key])
    || (field.type === 'json' && isJsonInvalid(jsonText(values[field.key])))
    // A secret whose commit failed keeps the text it was typed with; reveal it, wherever the group was folded.
    || secretRefused.has(field.key)
  ));

  // Settings → Access: only the manifest-declared credentials, grouped by their declared section when
  // they span more than one, so two "Username" rows stay told apart.
  if (mode === 'credentials') {
    const groups: { section?: PluginConfigField; fields: PluginConfigField[] }[] = [];
    let section: PluginConfigField | undefined;
    for (const field of detail.configSchema) {
      if (field.type === 'section') { section = field; continue; }
      if (!credentialKeys.has(field.key)) continue;
      const last = groups[groups.length - 1];
      if (last && last.section === section) last.fields.push(field);
      else groups.push({ section, fields: [field] });
    }
    if (groups.length < 2) return <>{refusal}{fieldRows(groups[0]?.fields ?? [])}</>;
    return (
      <>
        {refusal}
        {groups.map((group, index) => (
          <FieldSet key={group.section?.key ?? `lead-${index}`} className="gap-6">
            {group.section ? <FieldLegend variant="label">{fieldLabel(group.section)}</FieldLegend> : null}
            {fieldRows(group.fields)}
          </FieldSet>
        ))}
      </>
    );
  }

  // Preserve section boundaries; section keys and labels select Setup or Advanced, with the advanced
  // flag taking precedence. Required and secret inputs select Setup; other advanced inputs select Advanced.
  // Credentials have exactly one editing place, Settings → Access; every other mode leaves them out.
  const schema = withoutFields(detail.configSchema, credentialKeys);
  let sectionMode: PluginConfigMode = 'behavior';
  const classified = schema.map((field) => {
    if (field.type === 'section') {
      const key = `${field.key} ${field.label}`.toLowerCase();
      sectionMode = field.advanced ? 'advanced' : /connection|setup|auth|credential/.test(key) ? 'setup' : /advanced/.test(key) ? 'advanced' : 'behavior';
      return { field, mode: sectionMode };
    }
    // Every required input belongs to Setup regardless of its key or the manifest author's language;
    // otherwise the checklist can say "missing" while hiding the only control on another tab.
    if (field.required || sectionMode === 'setup' || field.type === 'secret') return { field, mode: 'setup' as const };
    if (field.advanced) return { field, mode: 'advanced' as const };
    return { field, mode: sectionMode };
  });
  const visibleSchema = mode === 'all' ? schema : classified.filter((entry, index) => {
    if (entry.field.type !== 'section') return entry.mode === mode;
    const nextSection = classified.findIndex((next, nextIndex) => nextIndex > index && next.field.type === 'section');
    const children = classified.slice(index + 1, nextSection === -1 ? classified.length : nextSection);
    const hasModeChild = children.some((child) => child.field.type !== 'section' && child.mode === mode);
    // A terminal section with no inputs is intentional documentation.
    // Keep it on its declared tab instead of silently dropping its HelpTip.
    return hasModeChild || (children.length === 0 && entry.mode === mode);
  }).map((entry) => entry.field);
  if (layout === 'rows') return <>{refusal}{fieldRows(visibleSchema.filter((field) => field.type !== 'section'))}</>;
  const hasExplicitSections = schema.some((f) => f.type === 'section');
  const isConnection = (f: PluginConfigField) => f.type === 'secret';
  const connectionFields = visibleSchema.filter(isConnection);
  const behaviorFields = visibleSchema.filter((f) => !isConnection(f));
  // Groups start open and are remembered per plugin; missing inputs and field errors force a group open
  // even over a remembered fold, so they stay reachable.
  const group = (key: string, Icon: LucideIcon, title: string, hint: string | undefined, fields: PluginConfigField[]) => {
    const rows = fieldRows(fields);
    if (rows.length === 0) return null;
    return (
      <SettingsGroup
        key={key}
        title={title}
        hint={hint}
        icon={Icon}
        collapsible
        forceOpen={needsAttention(fields)}
        storageKey={`plugin.${name}.${mode}.${key}`}
      >
        {rows}
      </SettingsGroup>
    );
  };

  // Split the schema into author-declared sections so each becomes its own settings-group card (icon-chip
  // header) instead of one long form separated by naked divider lines. Fields before the first section
  // (rare) fall into a leading headerless card. The section's manifest hint stays behind a `?` affordance
  // in the header — never a visible description — matching the compact per-field help convention.
  const sectionCards: { section?: PluginConfigField; fields: PluginConfigField[] }[] = [];
  for (const f of visibleSchema) {
    if (f.type === 'section') sectionCards.push({ section: f, fields: [] });
    else {
      if (sectionCards.length === 0) sectionCards.push({ fields: [] });
      sectionCards[sectionCards.length - 1]!.fields.push(f);
    }
  }

  // A FRAGMENT, not a box. The cards below are section cards exactly like the ones on every core settings
  // page, and they have to be SIBLINGS inside the enclosing `SettingsDocument` to get that page's gap. A
  // wrapper div here made them one flex child of the document instead, which is how the plugin header card
  // and "Behavior" ended up welded together inside a single bordered rectangle with no gap between them.
  // The fields Access owns are gone from this form, so it says where they went and how they stand.
  const accessCard = credentials && credentialKeys.size > 0 && (mode === 'setup' || mode === 'all')
    ? <PluginAccessCard plugin={name} status={pluginAccessStatus(detail, credentials, values)} />
    : null;
  return (
    <>
      {refusal}
      {accessCard}
      {visibleSchema.length === 0 && (mode === 'setup' || mode === 'advanced' || (mode === 'all' && detail.configSchema.length > 0)) ? null : visibleSchema.length === 0 ? (
        <SettingsGroup icon={SlidersHorizontal} title={t.pluginDetail.config}>
          <EmptyState title={mode === 'behavior' ? t.pluginDetail.behaviorEmpty : t.pluginDetail.configEmpty} />
        </SettingsGroup>
      ) : hasExplicitSections ? (
        // Each declared section has compact header help and its visible settings records.
        sectionCards.map((card, i) => {
          const hint = card.section
            ? [...new Set([fieldHint(card.section), fieldHelp(card.section)].map((text) => text?.trim()).filter(Boolean))].join('\n\n') || undefined
            : undefined;
          const rows = fieldRows(card.fields);
          // Empty declared sections are information cards; sections with hidden inputs have no disclosure.
          if (card.fields.length > 0 && rows.length === 0) return null;
          return (
            <SettingsGroup
              key={card.section?.key ?? `lead-${i}`}
              icon={sectionIcon(card.section)}
              title={card.section ? fieldLabel(card.section) : undefined}
              hint={hint}
              collapsible={Boolean(card.section) && rows.length > 0}
              forceOpen={needsAttention(card.fields)}
              storageKey={card.section ? `plugin.${name}.${mode}.${card.section.key}` : undefined}
            >
              {rows}
            </SettingsGroup>
          );
        })
      ) : (
        <>
          {connectionFields.length ? group('connection', Link2, t.pluginCfg.sectionConnection, t.pluginCfg.sectionConnectionHint, connectionFields) : null}
          {behaviorFields.length ? group('behavior', SlidersHorizontal, t.pluginCfg.sectionBehavior, undefined, behaviorFields) : null}
        </>
      )}
    </>
  );
}
